import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useGuardDashboard } from "hooks/useGuard";

export default function TrackingGuardDashboardPage() {
  const navigate = useNavigate();
  const { summary, alerts, isLoading, error } = useGuardDashboard();

  return (
    <TrackingPageLayout
      title="O360-Guard – Dashboard"
      withWhiteBox={false}
      action={
        <Button size="xl" variant="outline" colorScheme="red_origin" fontWeight={700} borderRadius="8px" onClick={() => navigate("/tracking/guard/daily-report")}>
          Rapport quotidien
        </Button>
      }
    >
      {error && (
        <Box mb={4} p={4} bg="red.50" borderRadius="md">
          <Text color="red.700">
            Erreur lors du chargement des données Guard. Vérifiez que vous avez les droits Guard.
          </Text>
        </Box>
      )}

      {isLoading ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
            <Stat p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <StatLabel>Agents en poste</StatLabel>
              <StatNumber>{summary.agents_on_duty}</StatNumber>
            </Stat>
            <Stat p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <StatLabel>Incidents ouverts</StatLabel>
              <StatNumber>{summary.incidents_open}</StatNumber>
            </Stat>
            <Stat p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <StatLabel>Rondes en cours</StatLabel>
              <StatNumber>{summary.patrols_in_progress}</StatNumber>
            </Stat>
            <Stat p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <StatLabel>Rondes prévues aujourd&apos;hui</StatLabel>
              <StatNumber>{summary.patrols_planned_today}</StatNumber>
            </Stat>
            <Stat p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <StatLabel>Rondes terminées aujourd&apos;hui</StatLabel>
              <StatNumber>{summary.patrols_completed_today}</StatNumber>
            </Stat>
          </SimpleGrid>

          <Flex gap={4} flexWrap="wrap">
            <Box flex="1" minW="260px" p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <Heading size="sm" mb={3}>
                Incidents – SLA imminent
              </Heading>
              {alerts.sla_imminent.length === 0 ? (
                <Text fontSize="sm" color="gray.600">
                  Aucun incident en approche SLA.
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {alerts.sla_imminent.map((item: any, idx: number) => (
                    <Box key={item.id ?? idx} p={2} borderWidth="1px" borderRadius="md">
                      <Text fontWeight="medium">
                        {item.incident_type_name ?? "Incident"} – {item.site_name ?? "Site inconnu"}
                      </Text>
                      {item.sla_deadline && (
                        <Text fontSize="xs" color="gray.600">
                          Échéance SLA : {new Date(item.sla_deadline).toLocaleString("fr-FR")}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>

            <Box flex="1" minW="260px" p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="sm">
              <Heading size="sm" mb={3}>
                Incidents – SLA dépassé
              </Heading>
              {alerts.sla_depassement.length === 0 ? (
                <Text fontSize="sm" color="gray.600">
                  Aucun SLA dépassé.
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {alerts.sla_depassement.map((item: any, idx: number) => (
                    <Box key={item.id ?? idx} p={2} borderWidth="1px" borderRadius="md">
                      <HStack justify="space-between">
                        <Text fontWeight="medium">
                          {item.incident_type_name ?? "Incident"} – {item.site_name ?? "Site inconnu"}
                        </Text>
                        <Badge colorScheme="red" fontSize="xs">
                          SLA dépassé
                        </Badge>
                      </HStack>
                      {item.sla_deadline && (
                        <Text fontSize="xs" color="gray.600">
                          Échéance : {new Date(item.sla_deadline).toLocaleString("fr-FR")}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </Flex>
        </>
      )}
    </TrackingPageLayout>
  );
}

